import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Star,
  ChevronLeft,
  Loader2,
  Flag,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { getAllReviews, moderateReview } from "../lib/api";
import type { ReviewWithRelations } from "../lib/types";
import { toast } from "sonner";

interface ModerationQueueProps {
  onNavigate?: (page: string) => void;
}

export function ModerationQueue({ onNavigate }: ModerationQueueProps) {
  const { user, profile } = useAuth();
  const [reviews, setReviews] = useState<ReviewWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [moderatingId, setModeratingId] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<"pending" | "flagged" | "all">("pending");
  const [moderationNote, setModerationNote] = useState("");
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const data = await getAllReviews();
      setReviews(data);
    } catch (error: any) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (reviewId: string, action: "approve" | "reject") => {
    setModeratingId(reviewId);
    try {
      await moderateReview(reviewId, {
        mod_state: action === "approve" ? "approved" : "rejected",
        mod_notes: moderationNote || undefined,
      });

      toast.success(`Review ${action}d successfully`);
      
      // Refresh reviews
      await fetchReviews();
      setModerationNote("");
      setSelectedReviewId(null);
    } catch (error: any) {
      console.error("Error moderating review:", error);
      toast.error(error.message || `Failed to ${action} review`);
    } finally {
      setModeratingId(null);
    }
  };

  // Check if user is admin or moderator
  if (!user || !profile || (profile.role !== "admin" && profile.role !== "moderator")) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-4xl mx-auto text-center py-20">
          <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            You must be an administrator or moderator to access this page
          </p>
          <Button onClick={() => onNavigate && onNavigate("discover")}>
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }

  // Filter reviews
  const filteredReviews = reviews.filter((review) => {
    if (selectedFilter === "pending") {
      return review.mod_state === "pending";
    } else if (selectedFilter === "flagged") {
      return review.is_flagged;
    }
    return true;
  });

  const stats = {
    pending: reviews.filter(r => r.mod_state === "pending").length,
    flagged: reviews.filter(r => r.is_flagged).length,
    total: reviews.length,
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            variant="ghost"
            className="mb-6 hover:bg-primary/10"
            onClick={() => onNavigate && onNavigate("admin-dashboard")}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-6 h-6 text-primary" />
            <Badge className="gradient-green text-white">Moderator</Badge>
          </div>
          <h1 className="text-4xl mb-3">Moderation Queue</h1>
          <p className="text-xl text-muted-foreground">
            Review and moderate user-submitted content
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-6"
        >
          <div className="flex gap-3 flex-wrap">
            <Button
              variant={selectedFilter === "pending" ? "default" : "outline"}
              onClick={() => setSelectedFilter("pending")}
              className={selectedFilter === "pending" ? "gradient-green text-white" : ""}
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              Pending
              <Badge variant="secondary" className="ml-2">
                {stats.pending}
              </Badge>
            </Button>
            <Button
              variant={selectedFilter === "flagged" ? "default" : "outline"}
              onClick={() => setSelectedFilter("flagged")}
              className={selectedFilter === "flagged" ? "gradient-green text-white" : ""}
            >
              <Flag className="w-4 h-4 mr-2" />
              Flagged
              <Badge variant="secondary" className="ml-2">
                {stats.flagged}
              </Badge>
            </Button>
            <Button
              variant={selectedFilter === "all" ? "default" : "outline"}
              onClick={() => setSelectedFilter("all")}
              className={selectedFilter === "all" ? "gradient-green text-white" : ""}
            >
              All Reviews
              <Badge variant="secondary" className="ml-2">
                {stats.total}
              </Badge>
            </Button>
          </div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-32 w-full" />
              </Card>
            ))}
          </div>
        )}

        {/* Reviews List */}
        {!loading && (
          <div className="space-y-4">
            {filteredReviews.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Card className="p-12 glass-card text-center">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-primary" />
                  <h3 className="text-2xl mb-2">All caught up!</h3>
                  <p className="text-muted-foreground">
                    {selectedFilter === "pending"
                      ? "No pending reviews to moderate"
                      : selectedFilter === "flagged"
                      ? "No flagged reviews"
                      : "No reviews in the queue"}
                  </p>
                </Card>
              </motion.div>
            ) : (
              filteredReviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="p-6 glass-card">
                    <div className="space-y-4">
                      {/* Review Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-medium">
                              {review.product?.product_name || "Unknown Product"}
                            </h3>
                            <Badge className="capitalize">{review.product?.form}</Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < review.stars
                                      ? "fill-current text-primary"
                                      : "text-muted-foreground"
                                  }`}
                                />
                              ))}
                            </div>
                            <span>•</span>
                            <span>by {review.author?.display_name || "Anonymous"}</span>
                            <span>•</span>
                            <span>{new Date(review.submitted_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {review.is_flagged && (
                            <Badge variant="outline" className="border-red-500 text-red-500">
                              <Flag className="w-3 h-3 mr-1" />
                              Flagged
                            </Badge>
                          )}
                          <Badge
                            variant={
                              review.mod_state === "approved"
                                ? "default"
                                : review.mod_state === "rejected"
                                ? "destructive"
                                : "secondary"
                            }
                          >
                            {review.mod_state}
                          </Badge>
                        </div>
                      </div>

                      {/* Review Content */}
                      {review.title && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Title</p>
                          <p className="font-medium">{review.title}</p>
                        </div>
                      )}

                      {review.body && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Review</p>
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {review.body}
                          </p>
                        </div>
                      )}

                      {review.batch && (
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Batch #{review.batch.batch_no}
                          </p>
                        </div>
                      )}

                      {/* Moderation Notes */}
                      {review.mod_notes && (
                        <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                          <p className="text-xs text-muted-foreground mb-1">Moderation Notes</p>
                          <p className="text-sm">{review.mod_notes}</p>
                        </div>
                      )}

                      {/* Moderation Actions */}
                      {review.mod_state === "pending" && (
                        <div className="pt-4 border-t border-border/50 space-y-3">
                          {selectedReviewId === review.id && (
                            <div>
                              <Label className="text-sm text-muted-foreground mb-2 block">
                                Moderation Notes (Optional)
                              </Label>
                              <Textarea
                                value={moderationNote}
                                onChange={(e) => setModerationNote(e.target.value)}
                                placeholder="Add any notes about this decision..."
                                className="min-h-[80px]"
                              />
                            </div>
                          )}

                          <div className="flex gap-3">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  className="flex-1 gradient-green hover:opacity-90 transition-opacity"
                                  disabled={moderatingId === review.id}
                                  onClick={() => setSelectedReviewId(review.id)}
                                >
                                  {moderatingId === review.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                  )}
                                  Approve
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Approve this review?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This review will be published and visible to all users.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleModerate(review.id, "approve")}
                                    className="bg-primary hover:bg-primary/90"
                                  >
                                    Approve Review
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  className="flex-1"
                                  disabled={moderatingId === review.id}
                                  onClick={() => setSelectedReviewId(review.id)}
                                >
                                  {moderatingId === review.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <XCircle className="w-4 h-4 mr-2" />
                                  )}
                                  Reject
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Reject this review?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This review will be rejected and hidden from public view. The author
                                    will not be notified.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleModerate(review.id, "reject")}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    Reject Review
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      )}

                      {/* Already Moderated */}
                      {review.mod_state !== "pending" && (
                        <div className="pt-4 border-t border-border/50">
                          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                            <span className="text-sm text-muted-foreground">
                              This review has been {review.mod_state}
                            </span>
                            {review.mod_state === "approved" ? (
                              <CheckCircle className="w-5 h-5 text-primary" />
                            ) : (
                              <XCircle className="w-5 h-5 text-destructive" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Add Label import at the top
import { Label } from "../components/ui/label";
